<template>
  <o-modal
    ref="modal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.save'), type: 'confirm', disabled: isDisabled, callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full p-2 flex flex-col"
  >
    <div v-if="purchaseOrderItem" class="text-left h-full flex flex-col">
      <div class="text-center">
        <h3 class="font-medium leading-6 text-md text-gray-700 dark:text-gray-200 mb-1">
          {{ title }}
        </h3>

        <p class="text-sm leading-5 text-gray-600 dark:text-gray-300">
          {{ $t('purchaseOrder.editProduct') }}
        </p>
      </div>

      <div class="bg-white dark:bg-gray-900 border-t border-b -mx-8 mb-4 mt-6 flex-grow h-10">
        <o-scroll class="py-4 px-8 h-full">
          <o-form ref="form" class="h-full">
            <div class="flex space-x-4 mb-6">
              <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('general.price')" :info="$t('components.purchaseOrder.actions.edit.priceInfo')">
                <currency-field v-model="purchaseOrderItem.price" :status="status" class="w-full shadow-sm" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('components.purchaseOrder.actions.edit.orderQuantity')" :info="$t('components.purchaseOrder.actions.edit.orderQuantityInfo')">
                <number-field v-model="purchaseOrderItem.orderQuantity" :status="status" class="w-full shadow-sm" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('general.notes')">
                <text-area-field v-model="purchaseOrderItem.notes" class="w-full shadow-sm" />
              </o-field-group>
            </div>

            <div>
              <label class="block text-sm text-gray-700 dark:text-gray-200 font-medium" tabindex="0">
                <span>{{ $t('general.taxes') }}</span>
              </label>
              <p class="block text-gray-600 dark:text-gray-300 text-sm mb-6">
                {{ $t('components.purchaseOrder.actions.edit.taxesInfo') }}
              </p>

              <o-field-group
                v-for="tax in purchaseOrderItem.taxes"
                :key="tax.tax.idTax"
                v-slot="{ status }"
                :label="tax.tax.name"
                :name="tax.tax.name"
                :info="$t('tax.includeInItemCost', { name: tax.tax.name, amount: tax.tax.amountLabel })"
                class="mb-4 block"
                col
                reverse
              >
                <toggle-field v-model="tax.enabled" :status="status" />
              </o-field-group>
            </div>
          </o-form>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import { OFieldGroup, OForm } from '@/components/elements'
import { CurrencyField, TextAreaField, NumberField, ToggleField } from '@/components/fields'

import type { PurchaseOrder, PurchaseOrderItem } from '~/_api/models/purchaseOrder'

interface Props {
  purchaseOrder: PurchaseOrder
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const modal = ref<any>()
const form = ref<any>()
const title = ref('')
const purchaseOrderItem = ref<PurchaseOrderItem | null>(null)

const isDisabled = computed(() => {
  return false
})

const fetchPurchaseOrderItem = async (item: PurchaseOrderItem) => {
  try {
    const response = await $api.purchaseOrder.getItem(props.purchaseOrder, item)
    purchaseOrderItem.value = response.data
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
}

const open = async (item: PurchaseOrderItem) => {
  title.value = (item as any).title
  await fetchPurchaseOrderItem(item)

  modal.value.open()
}

const validate = () => {
  return form.value.validate()
}

const handleConfirm = async () => {
  if (!purchaseOrderItem.value) return

  emit('update:loading', true)

  try {
    const response = await $api.purchaseOrder.saveItem(props.purchaseOrder, purchaseOrderItem.value)

    if (response.success) {
      $notification.toast({
        title: $i18n.t('purchaseOrder.saved'),
        text: $i18n.t('components.purchaseOrder.actions.edit.successInfo'),
        variant: 'success',
      })

      emit('success')

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
    emit('error')
  }
}

defineExpose({ open })
</script>
